// Bloom design tokens — single source of truth for the showcase page.

export const PALETTE = {
  primary: [
    { name: "Primary / Orange", role: "Primary actions, focus, brand", varName: "--primary", light: "#FF7A45", dark: "#FF8C5A", onLight: "text-white", onDark: "text-[#1B202D]" },
    { name: "Primary Strong", role: "Orange text on white (AA)", varName: "--primary-strong", light: "#C2410C", dark: "#FFA374", onLight: "text-white", onDark: "text-[#1B202D]" },
  ],
  secondary: [
    { name: "Sunshine Yellow", role: "Highlights, rewards, streaks", varName: "--secondary", light: "#FFD166", dark: "#FFD97D", onLight: "text-[#2B2D42]", onDark: "text-[#1B202D]" },
    { name: "Teal", role: "Success, progress, growth", varName: "--teal", light: "#06D6A0", dark: "#20E2B3", onLight: "text-[#05372C]", onDark: "text-[#052B22]" },
    { name: "Blue", role: "Information, links, calm focus", varName: "--blue", light: "#118AB2", dark: "#2CB4E0", onLight: "text-white", onDark: "text-[#06231F]" },
  ],
  neutral: [
    { name: "Background", role: "App surface (warm white)", varName: "--background", light: "#FFFCF9", dark: "#1B202D", onLight: "text-[#2B2D42]", onDark: "text-white" },
    { name: "Card", role: "Raised content surface", varName: "--card", light: "#FFFFFF", dark: "#232A3B", onLight: "text-[#2B2D42]", onDark: "text-white" },
    { name: "Muted", role: "Subtle fills & tracks", varName: "--muted", light: "#F4F1EE", dark: "#2C344A", onLight: "text-[#2B2D42]", onDark: "text-white" },
    { name: "Foreground", role: "Primary text (ink navy)", varName: "--foreground", light: "#2B2D42", dark: "#F8F9FA", onLight: "text-white", onDark: "text-[#1B202D]" },
    { name: "Muted Foreground", role: "Secondary text", varName: "--muted-foreground", light: "#5B6678", dark: "#A0ABC0", onLight: "text-white", onDark: "text-[#1B202D]" },
    { name: "Border", role: "Hairlines & dividers", varName: "--border", light: "#EAE6E1", dark: "#2C344A", onLight: "text-[#2B2D42]", onDark: "text-white" },
  ],
  semantic: [
    { name: "Success", role: "Correct answer, complete", varName: "--success", light: "#06D6A0", dark: "#20E2B3", onLight: "text-[#05372C]", onDark: "text-[#052B22]" },
    { name: "Info", role: "Hints & guidance", varName: "--info", light: "#118AB2", dark: "#2CB4E0", onLight: "text-white", onDark: "text-[#06231F]" },
    { name: "Warning", role: "Gentle caution", varName: "--warning", light: "#E8920C", dark: "#F6B24C", onLight: "text-white", onDark: "text-[#1B202D]" },
    { name: "Encourage", role: "Try again — never harsh", varName: "--destructive", light: "#EF476F", dark: "#FF6B8B", onLight: "text-white", onDark: "text-[#1B202D]" },
  ],
};

export const TYPE_SCALE = [
  { token: "Display", sample: "Let's learn", cls: "text-5xl md:text-6xl font-display font-black tracking-tight", note: "Hero moments only" },
  { token: "H1", sample: "Today's Adventure", cls: "text-4xl md:text-5xl font-display font-extrabold tracking-tight", note: "Page titles" },
  { token: "H2", sample: "Reading Quest", cls: "text-2xl md:text-3xl font-display font-bold", note: "Section titles" },
  { token: "H3", sample: "Chapter Three", cls: "text-xl md:text-2xl font-display font-bold", note: "Card & group titles" },
  { token: "Body Large", sample: "Read the sentence and choose the missing word.", cls: "text-lg md:text-xl font-medium leading-relaxed", note: "Lesson instructions" },
  { token: "Body", sample: "Tap the picture that matches the sound you hear.", cls: "text-base font-medium leading-relaxed", note: "Default UI text" },
  { token: "Label", sample: "DAILY STREAK", cls: "text-sm font-bold uppercase tracking-wider", note: "Eyebrows & badges" },
  { token: "Caption", sample: "Saved a moment ago", cls: "text-sm font-semibold text-muted-foreground", note: "Meta & helper text" },
];

export const SPACING = [
  { token: "2xs", px: 4, cls: "1" },
  { token: "xs", px: 8, cls: "2" },
  { token: "sm", px: 12, cls: "3" },
  { token: "md", px: 16, cls: "4" },
  { token: "lg", px: 24, cls: "6" },
  { token: "xl", px: 32, cls: "8" },
  { token: "2xl", px: 48, cls: "12" },
  { token: "3xl", px: 64, cls: "16" },
  { token: "4xl", px: 96, cls: "24" },
];

export const RADII = [
  { token: "sm", value: "8px", cls: "rounded-sm", use: "Tags, chips" },
  { token: "md", value: "12px", cls: "rounded-md", use: "Inner elements" },
  { token: "lg", value: "16px", cls: "rounded-lg", use: "Inputs, small cards" },
  { token: "xl", value: "20px", cls: "rounded-xl", use: "Buttons (sm)" },
  { token: "2xl", value: "24px", cls: "rounded-2xl", use: "Inputs, tiles" },
  { token: "3xl", value: "32px", cls: "rounded-3xl", use: "Cards, sheets" },
  { token: "full", value: "999px", cls: "rounded-full", use: "Buttons, avatars, pills" },
];

export const ELEVATION = [
  { token: "Flat", cls: "shadow-none border border-border", desc: "Resting tiles, inputs", value: "border only" },
  { token: "Soft", cls: "shadow-soft", desc: "Cards & surfaces", value: "0 8px 30px / 6%" },
  { token: "Lifted", cls: "shadow-soft-lg", desc: "Modals, hovered cards", value: "0 20px 50px / 10%" },
  { token: "Glow", cls: "shadow-glow", desc: "Primary buttons", value: "orange 28%" },
];

export const BREAKPOINTS = [
  { token: "Base", range: "0–639px", device: "Phone (mobile-first)", cols: "1 column" },
  { token: "sm", range: "≥ 640px", device: "Large phone", cols: "1–2 columns" },
  { token: "md", range: "≥ 768px", device: "Tablet", cols: "2 columns + rail" },
  { token: "lg", range: "≥ 1024px", device: "Laptop", cols: "Sidebar + content" },
  { token: "xl", range: "≥ 1280px", device: "Desktop", cols: "Max content 72rem" },
];

export const MOTION = [
  { token: "Instant", ms: "120ms", use: "Taps, toggles, presses", ease: "ease-out" },
  { token: "Quick", ms: "200ms", use: "Hovers, colour shifts", ease: "ease-out" },
  { token: "Standard", ms: "300ms", use: "Cards, reveals, sheets", ease: "bounce" },
  { token: "Expressive", ms: "500ms", use: "Celebrations & rewards", ease: "bounce" },
];
